import React, { useContext, useRef, useState } from 'react';
import { ErrorContext } from '../../services/auth/error.provider';
import { Era835Summary, PAYMENT_METHOD_LABELS, summarize835 } from '../../services/era-835/era-835';
import { ToastContext } from '../../services/toast/toast';
import { BaseButton } from '../base/button';
import { BatchFileUploader } from '../batch-document-upload/batch-file-uploader';
import { renderAmount } from '../data-table/components/cells/cells';
import * as S from './era-upload-dialog.styles';

export interface IEraUploadDialogProps {
    /** Shown in the intro; the id lives in `upload`, which already knows where to send the file. */
    clearinghouseName?: string;
    /**
     * Sends the file's text to the app's own endpoint — `postProviderX12835Era` in the provider app,
     * `postX12835Era` in support. Each app keeps its own store call; only the dialog is shared.
     */
    upload: (x12: string) => Promise<unknown>;
    /** Reports a failed upload. Defaults to the core ErrorContext, which support mounts. */
    onError?: (err: Error) => void;
    onClose: () => void;
}

interface ISelectedFile {
    file: File;
    content: string;
    summary: Era835Summary;
}

/**
 * Uploads an X12 835 to one clearinghouse — `POST /{provider|support}/clearinghouse/{id}/era/x12/5010/835`,
 * which creates an ERA from the file and answers with its id.
 *
 * The file is checked in the browser before the button enables (see era-835.ts), because the
 * endpoint will create an ERA from whatever it is sent. Two things this cannot check, and which the
 * operator is told about instead: whether this remittance was already uploaded — the request has no
 * idempotency key, so a second upload may make a second ERA — and anything past the payment
 * header, which is patient data and is not read here.
 */
export const EraUploadDialog: React.FC<IEraUploadDialogProps> = ({ clearinghouseName, upload, onError, onClose }) => {
    const errorContext = useContext(ErrorContext);
    const toastContext = useContext(ToastContext);
    /** What the picker shows — set at once, before the file has been read. */
    const [file, setFile] = useState<File>();
    /** The same file once read and checked; lags `file` by one read. */
    const [selected, setSelected] = useState<ISelectedFile>();
    const [readError, setReadError] = useState<string>();
    const [uploading, setUploading] = useState(false);
    /** The file whose read should win — a slower read of an earlier pick must not overwrite it. */
    const latestFile = useRef<File | undefined>(undefined);
    /** A ref, not state: two clicks inside one render would both see `uploading === false`. */
    const inFlight = useRef(false);

    /** `files` is [] after Remove / Clear all, and at most one file otherwise (maxFiles={1}). */
    const handleFilesChange = async (files: File[]): Promise<void> => {
        const next = files[0];
        latestFile.current = next;
        setFile(next);
        setSelected(undefined);
        setReadError(undefined);
        if (!next) {
            return;
        }
        try {
            const content = await next.text();
            if (latestFile.current === next) {
                setSelected({ file: next, content, summary: summarize835(content) });
            }
        } catch {
            if (latestFile.current === next) {
                setReadError('The file could not be read.');
            }
        }
    };

    const handleUpload = async (): Promise<void> => {
        if (inFlight.current || !selected || selected.summary.error) {
            return;
        }
        inFlight.current = true;
        setUploading(true);
        try {
            await upload(selected.content);
            toastContext.notify?.(`ERA uploaded to ${clearinghouseName || 'the clearinghouse'}.`, 'left');
            onClose();
        } catch (err) {
            // The dialog stays open with the same file, so the operator can read the error and decide.
            (onError || errorContext.handleError)(err as Error);
        } finally {
            inFlight.current = false;
            setUploading(false);
        }
    };

    const summary = selected?.summary;
    const uploadable = !!summary && !summary.error;
    const payments = summary?.payments || [];
    const errorMessage = readError || summary?.error;
    const recognised = uploadable
        ? `X12 835 · ${summary?.version} · ${payments.length} payment${payments.length === 1 ? '' : 's'}`
        : undefined;

    return (
        <S.DialogBody>
            <S.Intro>
                Creates an ERA in <strong>{clearinghouseName || 'this clearinghouse'}</strong> from an X12 835
                remittance file (version 5010).
            </S.Intro>
            {/* The provider's document-upload picker, capped at one file. `accept=""` turns its type
                filter off — its default is document types, and 835s arrive as .835, .txt, .edi, .x12,
                .era, .rmt and more. The content check decides, not the extension. */}
            <BatchFileUploader
                files={file ? [file] : []}
                onChange={handleFilesChange}
                accept=""
                maxFiles={1}
                disabled={uploading}
                errorMessage={errorMessage}
            />
            {recognised && <S.Recognised>{recognised}</S.Recognised>}
            {!!payments.length && (
                <S.Payments>
                    <S.PaymentHeader>
                        <span>Payer</span>
                        <span>Amount</span>
                        <span>Method</span>
                        <span>Payment date</span>
                        <span>Check / EFT number</span>
                    </S.PaymentHeader>
                    {payments.map((payment, index) => (
                        <S.Payment key={`${payment.traceNumber}-${index}`}>
                            <span>{payment.payerName || '-'}</span>
                            <S.Mono>
                                {payment.paymentAmount ? renderAmount({ amount: payment.paymentAmount }) : '-'}
                            </S.Mono>
                            <span>
                                {payment.paymentMethod
                                    ? PAYMENT_METHOD_LABELS[payment.paymentMethod] || payment.paymentMethod
                                    : '-'}
                            </span>
                            <S.Mono>{payment.paymentDate || '-'}</S.Mono>
                            <S.Mono>{payment.traceNumber || '-'}</S.Mono>
                        </S.Payment>
                    ))}
                </S.Payments>
            )}
            {!!payments.length && (
                <S.Caution>
                    Uploading the same file twice may create a duplicate ERA. Check that{' '}
                    {payments.length === 1 ? 'this check / EFT number has' : 'these check / EFT numbers have'} not
                    already been uploaded.
                </S.Caution>
            )}
            <S.ButtonGroup>
                <BaseButton variant="outlined" size="medium" onClick={onClose} disabled={uploading}>
                    Cancel
                </BaseButton>
                <BaseButton variant="primary" size="medium" onClick={handleUpload} disabled={!uploadable || uploading}>
                    {uploading ? 'Uploading…' : 'Upload'}
                </BaseButton>
            </S.ButtonGroup>
        </S.DialogBody>
    );
};
